// FILE: src/components/modals/ProductDetailsModal.jsx

import React, { useState } from 'react';
import { X, Heart, ShoppingCart, Plus, Minus, Star, Package, Truck, Shield } from 'lucide-react';
import { getProductImage, getProductEmoji } from '../../utils/imageHelpers';

const ProductDetailsModal = ({ 
  isOpen, 
  onClose, 
  product, 
  addToCart, 
  showToast, 
  favorites = [], 
  toggleFavorite,
  setShowSubscription 
}) => {
  const [quantity, setQuantity] = useState(1);

  if (!isOpen || !product) return null;

  const isFav = Array.isArray(favorites) ? favorites.some(f => f.id === product.id) : false;

  const handleAddToCart = () => {
    for (let i = 0; i < quantity; i++) {
      addToCart(product);
    }
    showToast && showToast(`${quantity}x ${product.name} added to cart!`, 'success');
  };

  const handleFavoriteToggle = () => {
    if (typeof toggleFavorite === 'function') {
      toggleFavorite(product);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white w-full max-w-md mx-auto rounded-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex justify-between items-center rounded-t-2xl">
          <h2 className="text-xl font-bold text-gray-800">Product Details</h2>
          <button 
            onClick={onClose} 
            className="text-gray-500 hover:text-gray-700 p-2 hover:bg-gray-100 rounded-full transition-all"
          >
            <X size={24} />
          </button>
        </div>

        {/* Product Image */}
        <div className="px-6 pt-4">
          <div className="relative">
            {getProductImage(product, 'modal')}
            <button
              onClick={handleFavoriteToggle}
              className="absolute top-4 right-4 p-3 bg-white rounded-full shadow-lg hover:scale-105 transition-transform"
            >
              <Heart size={20} className={`${isFav ? 'text-pink-500' : 'text-gray-400'}`} fill={isFav ? 'currentColor' : 'none'} />
            </button>
          </div>
        </div>

        {/* Product Info */}
        <div className="p-6">
          <div className="mb-4">
            <h3 className="text-2xl font-bold text-gray-800 mb-2">{product.name}</h3>
            <div className="flex items-center gap-2 mb-3">
              {product.sale_price && product.sale_price < product.price ? (
                <>
                  <span className="text-2xl font-bold text-red-600">₹{product.sale_price}</span>
                  <span className="text-lg text-gray-400 line-through">₹{product.price}</span>
                  <span className="text-sm bg-red-100 text-red-600 px-2 py-1 rounded-full font-semibold">
                    {Math.round(((product.price - product.sale_price) / product.price) * 100)}% OFF
                  </span>
                </>
              ) : (
                <span className="text-2xl font-bold text-blue-600">₹{product.price}</span>
              )}
              {product.fat && (
                <span className="text-sm bg-green-100 text-green-700 px-3 py-1 rounded-full">
                  {product.fat} fat
                </span>
              )}
              {product.weight && !product.fat && (
                <span className="text-sm bg-blue-100 text-blue-700 px-3 py-1 rounded-full">
                  {product.weight}
                </span>
              )}
              {product.brand && !product.fat && !product.weight && (
                <span className="text-sm bg-gray-100 text-gray-700 px-3 py-1 rounded-full">
                  {product.brand}
                </span>
              )}
            </div>
          </div>

          {/* Description */}
          <div className="mb-6">
            <h4 className="text-lg font-semibold text-gray-800 mb-3">Description</h4>
            <p className="text-gray-600 leading-relaxed text-base">
              {product.description || 'Fresh and high-quality product delivered directly to your doorstep.'}
            </p>
          </div>

          {/* Product Features */}
          <div className="mb-6">
            <h4 className="text-lg font-semibold text-gray-800 mb-3">Features</h4>
            <div className="grid grid-cols-1 gap-3">
              <div className="flex items-center gap-3 text-gray-600">
                <div className="p-2 bg-green-100 rounded-full">
                  <Shield size={16} className="text-green-600" />
                </div>
                <span className="text-sm">100% Fresh & Natural</span>
              </div>
              <div className="flex items-center gap-3 text-gray-600">
                <div className="p-2 bg-blue-100 rounded-full">
                  <Truck size={16} className="text-blue-600" />
                </div>
                <span className="text-sm">Free Home Delivery</span>
              </div>
              <div className="flex items-center gap-3 text-gray-600">
                <div className="p-2 bg-purple-100 rounded-full">
                  <Package size={16} className="text-purple-600" />
                </div>
                <span className="text-sm">Hygienic Packaging</span>
              </div>
            </div>
          </div>

          {/* Quantity Selector */}
          <div className="mb-6">
            <h4 className="text-lg font-semibold text-gray-800 mb-3">Quantity</h4>
            <div className="flex items-center gap-4">
              <div className="flex items-center border border-gray-300 rounded-lg">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="p-3 hover:bg-gray-50 transition-colors"
                  disabled={quantity <= 1}
                >
                  <Minus size={16} className={quantity <= 1 ? 'text-gray-300' : 'text-gray-600'} />
                </button>
                <span className="px-4 py-3 font-medium text-gray-800 border-l border-r border-gray-300">
                  {quantity}
                </span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="p-3 hover:bg-gray-50 transition-colors"
                >
                  <Plus size={16} className="text-gray-600" />
                </button>
              </div>
              <div className="text-sm text-gray-500">
                Total: ₹{((product.sale_price && product.sale_price < product.price ? product.sale_price : product.price) * quantity).toFixed(2)}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={handleAddToCart}
              className="w-full bg-blue-500 text-white py-4 rounded-xl text-base font-semibold hover:bg-blue-600 transition-colors flex items-center justify-center gap-2"
            >
              <ShoppingCart size={20} />
              Add {quantity > 1 ? `${quantity} items` : 'to Cart'} - ₹{((product.sale_price && product.sale_price < product.price ? product.sale_price : product.price) * quantity).toFixed(2)}
            </button>

            {setShowSubscription && (
              <button
                onClick={() => {
                  setShowSubscription(product);
                  onClose();
                }}
                className="w-full bg-green-500 text-white py-4 rounded-xl text-base font-semibold hover:bg-green-600 transition-colors flex items-center justify-center gap-2"
              >
                🔔 Subscribe & Save 10%
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailsModal;